import { queryOptions } from "@tanstack/react-query";
import {
  type Api,
  getAthleteCalendar,
  getCalendar,
  getCalendarItem,
} from "api-client";

import {
  type CalendarDateRange,
  type CalendarWeekRange,
  CALENDAR_INITIAL_VISIBLE_WEEK_COUNT,
  getCalendarInitialWeeks,
  getCalendarWeekRangeDates,
} from "src/calendar/calendarWeeks";
import { type CalendarInitialView } from "src/lib/calendarViewRoute";

export type CalendarSource =
  | { type: "athleteCalendar"; athleteId: string }
  | {
      type: "calendar";
      athleteId: string | undefined;
      includeWorkflow: boolean;
    };

export interface CalendarInitialData {
  weeks: CalendarWeekRange;
  data: Api.GetCalendar.Output.Self;
}

export const OWN_CALENDAR_SOURCE: CalendarSource = {
  type: "calendar",
  athleteId: undefined,
  includeWorkflow: true,
};

export function getAthleteCalendarSource(
  athleteId: string,
  calendar: CalendarInitialView,
): CalendarSource {
  if (calendar.view === "month") {
    return { type: "athleteCalendar", athleteId };
  }

  return { type: "calendar", athleteId, includeWorkflow: true };
}

export async function fetchCalendarInitialData(
  source: CalendarSource,
  calendar: CalendarInitialView,
): Promise<CalendarInitialData> {
  const weeks = getCalendarInitialWeeks(
    calendar.initialWeekIndex,
    calendar.view === "week" ? 1 : CALENDAR_INITIAL_VISIBLE_WEEK_COUNT,
  );
  return {
    weeks,
    data: await fetchCalendarRange(source, getCalendarWeekRangeDates(weeks)),
  };
}

export async function fetchCalendarRange(
  source: CalendarSource,
  range: CalendarDateRange,
): Promise<Api.GetCalendar.Output.Self> {
  if (source.type === "athleteCalendar") {
    const response = await getAthleteCalendar({
      path: { athleteId: source.athleteId },
      query: { startDate: range.startDate, endDate: range.endDate },
    });
    return response.data.data;
  }

  const response = await getCalendar({
    query: {
      startDate: range.startDate,
      endDate: range.endDate,
      includeWorkflow: source.includeWorkflow,
      athleteId: source.athleteId,
    },
  });
  return response.data.data;
}

export function calendarItemQueryOptions(calendarItemId: string) {
  return queryOptions({
    queryKey: ["calendarItem", calendarItemId],
    queryFn: async () => {
      const response = await getCalendarItem({ path: { calendarItemId } });
      return response.data.data;
    },
  });
}
